import { ENV_VARS } from "@/constants/env";
import { API_PLATFORM_CONFIG_MAINTENANCE } from "@/constants/apiEndpoints";
import type { IMaintenanceCheckResult } from "@/types/maintenance.interface";

const MAINTENANCE_URL = `${ENV_VARS.API_INSTANCE}${API_PLATFORM_CONFIG_MAINTENANCE}`;

export type { IMaintenanceCheckResult };

// This call gates the ENTIRE app boot — nothing renders until it settles. A
// stopped/restarting API can leave the socket bound with nobody answering, so
// the request hangs indefinitely and the user stares at a blank white page.
// Cap it: unreachable API == "not in maintenance", carry on booting.
const MAINTENANCE_TIMEOUT_MS = 4_000;

const NOT_IN_MAINTENANCE: IMaintenanceCheckResult = {
  isActive: false,
  maintenanceMessage: null,
  isScheduled: false,
  scheduledStartAt: null,
  scheduledEndAt: null,
  scheduledMessage: null,
};

export async function fetchMaintenanceStatus(): Promise<IMaintenanceCheckResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), MAINTENANCE_TIMEOUT_MS);
  try {
    const res = await fetch(MAINTENANCE_URL, { signal: controller.signal });
    if (!res.ok) return NOT_IN_MAINTENANCE;
    const json = await res.json();
    return {
      isActive: json?.data?.isActive === true,
      maintenanceMessage: json?.data?.maintenanceMessage ?? null,
      isScheduled: json?.data?.isScheduled === true,
      scheduledStartAt: json?.data?.scheduledStartAt ?? null,
      scheduledEndAt: json?.data?.scheduledEndAt ?? null,
      scheduledMessage: json?.data?.scheduledMessage ?? null,
    };
  } catch {
    // Network error, or the 4s cap fired on a hung/stopped API.
    return NOT_IN_MAINTENANCE;
  } finally {
    clearTimeout(timeout);
  }
}
